/**
 * Design tokens lifted from the Dreams POS "Sales Dashboard" template
 * (html/template/assets/scss/utils/_variables.scss).
 *
 * The template is Bootstrap 5; this app is Ant Design. Rather than port markup — which would mean
 * rewriting every component and risking behaviour — the template's palette, radii, shadows and
 * typography are expressed here as antd theme tokens and fed to a single ConfigProvider. Every
 * component in the app picks the new look up automatically, with no change to its logic.
 *
 * Anything that cannot be expressed as a token (sidebar chrome, card headers, table headers) is
 * handled by the matching global layer in index.css.
 */
import type { ThemeConfig } from 'antd';

/** Raw template variables, kept at their source names so they can be diffed against the SCSS. */
export const palette = {
  primary: '#FE9F43',
  primaryHover: '#FD8B20',
  secondary: '#092C4C',
  success: '#3EB780',
  info: '#155EEF',
  warning: '#FFCA18',
  danger: '#FF0000',
  dark: '#1B2850',
  light: '#F9FAFB',
  purple: '#6938EF',
  pink: '#DD2590',
  teal: '#0E9384',
  cyan: '#06AED4',
  orange: '#E04F16',
  indigo: '#3538CD',

  // Tints used for subtle fills (badges, hovers, selected rows).
  primaryTransparent: '#FFF6EE',
  secondaryTransparent: '#E6EAED',
  successTransparent: '#ECFAF2',
  warningTransparent: '#FFFAEA',
  dangerTransparent: '#FFE8E8',
  infoTransparent: '#EAF1FE',
  purpleTransparent: '#F2EDFE',

  // Text / surfaces
  textColor: '#646B72',
  titleColor: '#212B36',
  subTitle: '#B8BCC9',
  bodyBg: '#FAFBFE',
  white: '#FFFFFF',
  borderColor: '#E6EAED',

  gray100: '#D3D5D7',
  gray400: '#90959B',
  gray600: '#646B72',
  gray900: '#212B36',
} as const;

export const shadows = {
  card: '0px 4px 60px 0px rgba(231, 231, 231, 0.47)',
  sm: '0px 4px 60px 0px rgba(190, 190, 190, 0.27)',
  lg: '0 5px 10px rgba(30, 32, 37, 0.12)',
  primary: '0px 4px 10px 0px rgba(254, 159, 67, 0.3)',
} as const;

/** The template sets $font-size-base: 14px with Nunito for body and Poppins for headings. */
const FONT_STACK =
  "'Nunito', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

export const salesTheme: ThemeConfig = {
  token: {
    colorPrimary: palette.primary,
    colorInfo: palette.info,
    colorSuccess: palette.success,
    colorWarning: palette.warning,
    colorError: palette.danger,
    colorLink: palette.primary,
    colorLinkHover: palette.primaryHover,

    colorText: palette.titleColor,
    colorTextSecondary: palette.textColor,
    colorTextTertiary: palette.gray400,
    colorTextDescription: palette.textColor,
    colorTextHeading: palette.titleColor,

    colorBgLayout: palette.bodyBg,
    colorBgContainer: palette.white,
    colorBorder: palette.borderColor,
    colorBorderSecondary: palette.borderColor,

    // $border-radius: 4px, with 8px on cards (see Card override below).
    borderRadius: 5,
    borderRadiusLG: 8,
    borderRadiusSM: 4,

    fontFamily: FONT_STACK,
    fontSize: 14,
    fontSizeHeading1: 32,
    fontSizeHeading2: 28,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,

    controlHeight: 38,
    boxShadow: shadows.card,
    boxShadowSecondary: shadows.sm,
    wireframe: false,
  },

  components: {
    Layout: {
      bodyBg: palette.bodyBg,
      headerBg: palette.white,
      headerHeight: 64,
      headerPadding: '0 24px',
      siderBg: palette.white,
      triggerBg: palette.white,
      triggerColor: palette.textColor,
    },

    Menu: {
      // Template sidebar is white with grey labels; the active item is solid primary orange.
      itemBg: palette.white,
      itemColor: palette.textColor,
      itemHoverBg: palette.primaryTransparent,
      itemHoverColor: palette.primary,
      itemSelectedBg: palette.primary,
      itemSelectedColor: palette.white,
      itemActiveBg: palette.primaryTransparent,
      itemBorderRadius: 5,
      itemHeight: 42,
      itemMarginInline: 12,
      subMenuItemBg: palette.white,
      iconSize: 16,
      collapsedIconSize: 18,
    },

    Card: {
      borderRadiusLG: 8,
      boxShadowTertiary: shadows.card,
      headerBg: palette.white,
      headerFontSize: 16,
      colorBorderSecondary: palette.borderColor,
      paddingLG: 20,
    },

    Button: {
      controlHeight: 38,
      borderRadius: 5,
      primaryShadow: shadows.primary,
      fontWeight: 600,
    },

    Table: {
      headerBg: palette.light,
      headerColor: palette.titleColor,
      headerSplitColor: 'transparent',
      borderColor: palette.borderColor,
      rowHoverBg: palette.primaryTransparent,
      cellPaddingBlock: 14,
      headerBorderRadius: 8,
    },

    Input: {
      controlHeight: 38,
      colorBorder: palette.borderColor,
      activeShadow: '0 0 0 2px rgba(254, 159, 67, 0.15)',
    },
    InputNumber: { controlHeight: 38, colorBorder: palette.borderColor },
    Select: { controlHeight: 38, colorBorder: palette.borderColor },
    DatePicker: { controlHeight: 38, colorBorder: palette.borderColor },

    Tabs: {
      inkBarColor: palette.primary,
      itemSelectedColor: palette.primary,
      itemHoverColor: palette.primaryHover,
      itemColor: palette.textColor,
      titleFontSize: 14,
      horizontalItemPadding: '12px 4px',
    },

    Tag: { borderRadiusSM: 4, defaultBg: palette.secondaryTransparent, defaultColor: palette.textColor },
    Statistic: { titleFontSize: 14, contentFontSize: 24 },
    Modal: { borderRadiusLG: 8, headerBg: palette.white, titleFontSize: 18 },
    Segmented: { itemSelectedBg: palette.primary, itemSelectedColor: palette.white, borderRadius: 5 },
    Progress: { defaultColor: palette.primary },
    Pagination: { itemActiveBg: palette.primary, borderRadius: 5 },
    Descriptions: { labelBg: palette.light, titleColor: palette.titleColor },
    Alert: { borderRadiusLG: 5 },
    Steps: { colorPrimary: palette.primary },
    Switch: { colorPrimary: palette.primary },
    Avatar: { colorTextPlaceholder: palette.primary },
  },
};

/**
 * Recharts and other non-antd surfaces read from here so charts stay in the same palette as the
 * rest of the UI instead of falling back to library defaults.
 */
export const chartColors = [
  palette.primary,
  palette.secondary,
  palette.success,
  palette.info,
  palette.purple,
  palette.cyan,
  palette.pink,
  palette.teal,
];
